import { Flex, Link, Spacer, Stack } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { Prisma } from '@prisma/client';
import { format, formatDistance } from 'date-fns';
import { useMemo } from 'react';
import { HiArrowRight } from 'react-icons/hi2';

import { trpc, TrpcOutputs } from '../config/trpc';
import { useCurrentRequest } from '../hooks/useCurrentRequest';

const Email = ({ from, to, subject, created_at, front_id }: TrpcOutputs['getFrontConversations'][0]['emails'][0]) => {
  const [fromEmails, toEmails] = useMemo(() => {
    return [from.map((x) => x.email), to.map((x) => x.email)];
  }, [from, to]);

  return (
    <Stack p="2">
      <Flex>
        <Link fontWeight="bold" target="_blank" href={`https://app.frontapp.com/open/${front_id}`}>
          {subject}
        </Link>
        <Spacer />
        <Flex title={format(created_at, 'PPpp')}>{formatDistance(created_at, new Date())} ago</Flex>
      </Flex>

      <Flex gap="3" alignItems="center">
        <Flex>{fromEmails.join(', ')}</Flex>
        <HiArrowRight />
        <Flex>{toEmails.join(', ')}</Flex>
      </Flex>
    </Stack>
  );
};

const renderConversation = (
  conversation: Prisma.FrontConversationGetPayload<{
    include: {
      emails: {
        include: {
          from: true;
          to: true;
          cc: true;
          bcc: true;
        };
      };
    };
  }>,
) => {
  const set =
    conversation?.emails?.reduce((agg, email) => {
      [...email.to, ...email.from].forEach((frontContact) => agg.add(frontContact.email));

      return agg;
    }, new Set<string>()) || new Set<string>();

  const participants = Array.from(set).toSorted();

  return (
    <Stack key={conversation.id}>
      <Flex>
        <Flex flexDirection="column">
          <Flex>
            <Flex fontWeight="bold">{conversation.subject}</Flex>
          </Flex>
          <Flex gap="3" alignItems="center">
            {participants.join(', ')}
          </Flex>
        </Flex>
        <Spacer />
        <Flex flexDir="column">
          <Flex title={format(conversation.emails[0].created_at, 'PPpp')}>
            Last email received {formatDistance(conversation.emails[0].created_at, new Date())} ago
          </Flex>
          <Flex title={format(conversation.created_at, 'PPpp')}>
            Created {formatDistance(conversation.created_at, new Date())} ago
          </Flex>
        </Flex>
      </Flex>
      <Stack background="blackAlpha.100" ml="4">
        {conversation.emails.map((email) => (
          <Email {...email} key={email.id} />
        ))}
      </Stack>
    </Stack>
  );
};

export const RequestEmailsScreen = () => {
  const { request } = useCurrentRequest();
  const { data: conversations, refetch } = trpc.getFrontConversations.useQuery(
    {
      request_id: request?.id,
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  trpc.onNewEmail.useSubscription(
    {
      request_id: request.id,
    },
    {
      onData: () => {
        refetch();
      },
    },
  );

  return (
    <Flex flex={1} overflowY="auto" flexDirection="column" minW="xl" bgColor="bg.subtle">
      <Stack p={4} fontSize="small">
        <Show if={!conversations?.length}>
          <Flex fontStyle="italic" fontSize="large">
            No emails for this request.
          </Flex>
        </Show>
        {conversations?.map(renderConversation)}
      </Stack>
    </Flex>
  );
};
